import AlertComponent from "@/components/sharedComponents/alert/alert.component";
import { EventBus } from "@/event-bus";
import { ModalNotification } from "@/interfaces/notification";
import { UserBiotrackConfig, UserMetrcConfig } from "@/interfaces/traceability";
import {
  pharmacistDefault,
  PharmacistLocationInterface,
  User,
  userDefault
} from "@/interfaces/user";
import {
  biotrackFormDetail,
  metrcFormDetail,
  pharmacistForm,
  profileForm,
  profileFormDetail
} from "@/metadata/user";
import { i18n } from "@/plugins/i18n";
import { messagesService } from "@/services/messages.service";
import { traceabilityService } from "@/services/traceability.service";
import { store } from "@/store";
import { Callback } from "@/types/types";
import {
  DynamicFormComponent,
  FormField,
  FormModel,
  FormResponse
} from "helix-vue-components";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import UserManagementFormComponent from "../../UserManagement/UserManagementForm/UserManagementForm.component";
import Template from "./ProfileManagementForm.template.vue";

const namespace: string = "UserModule";

@Component({
  mixins: [Template],
  components: {
    DynamicFormComponent
  }
})
export default class ProfileManagementFormComponent extends Vue {
  /**
   * fields to render
   */
  public fieldsConfig: FormField[] = profileForm;
  public fieldsConfigDetail: FormField[] = profileFormDetail;
  public form!: DynamicFormComponent;
  public formDetail!: DynamicFormComponent;
  public model = userDefault;
  public pharmacistDataModel = pharmacistDefault;
  public metrcConfig: UserMetrcConfig | null = null;
  public biotrackConfig: UserBiotrackConfig | null = null;
  public currentTraceability = {
    biotrackConfiguration: 0,
    metrcConfiguration: 0
  };
  public metrcProfileConfigDetail: FormField[] = metrcFormDetail;
  public biotrackProfileConfigDetail: FormField[] = biotrackFormDetail;
  public routeData!: string;
  public pharmacistTabDetails: FormField[] = pharmacistForm;
  public invalidDocument = false;
  public fileExtension = "";
  public fileSize = 0;
  public invalidFileName: boolean = false;

  /**
   * @method to save
   */
  @Getter("user", { namespace: "authModule" })
  public current!: User | null;

  @Getter("pharmacistInfo", { namespace })
  public pharmacistInfo!: FormModel | null;
  /**
   * @method to save
   */
  @Action("updateUser", { namespace: "authModule" })
  public saveUser!: Callback;

  @Getter("loading", { namespace: "authModule" })
  public loading!: Boolean;

  @Action("getPharmacistInfo", { namespace: "UserModule" })
  public getPharmacistInfo!: Callback;

  public formChange(form: any) {
    if (!form.model.avatar) {
      this.invalidDocument = false;
      return;
    }
    // getting file extension
    this.fileExtension = form.model.avatar.name.match(/\.([^\.]+)$/)![1];
    // getting file size in "mega bytes" by dividing with 1000000
    this.fileSize = form.model.avatar.size / 1000000;
    // only " alphanumeric,_ , - , ." characters are allowed in file name
    if (/([^a-zA-Z0-9_.\-])/.test(form.model.avatar.name)) {
      this.invalidFileName = true;
    } else {
      this.invalidFileName = false;
    }
    if (
      !store.getters["authModule/fileImageExtention"].includes(
        this.fileExtension
      ) ||
      this.fileSize > store.getters["authModule/fileUploadLimit"] ||
      this.invalidFileName
    ) {
      this.invalidDocument = true;
    } else {
      this.invalidDocument = false;
    }
  }
  public async save() {
    if (this.invalidDocument) {
      const invalidDocMsg: string | string[] = [];
      if (
        !store.getters["authModule/fileImageExtention"].includes(
          this.fileExtension
        )
      ) {
        invalidDocMsg.push(
          String(
            this.$t("security.invalid_image_files_extension", {
              fileExtention: store.getters["authModule/fileImageExtention"]
            })
          )
        );
      }
      if (this.fileSize > store.getters["authModule/fileUploadLimit"]) {
        invalidDocMsg.push(
          String(
            this.$t("security.invalid_file_size", {
              fileSize: store.getters["authModule/fileUploadLimit"]
            })
          )
        );
      }
      if (this.invalidFileName) {
        invalidDocMsg.push(String(this.$t("security.invalid_file_name")));
      }
      messagesService.showMessage(
        "fal fa-exclamation-triangle",
        invalidDocMsg,
        "error"
      );
      return;
    }
    const resp: FormResponse = await this.form.submit();
    const respDetail: FormResponse = await this.formDetail.submit();

    if (resp.valid && respDetail.valid) {
      const newModel: any = {
        ...resp.currentModel,
        ...respDetail.currentModel,
        routeCondition: this.routeData
      };
      newModel.redirectTo = "dashboard";
      if (isEqual(this.metrcConfig, newModel.metrc)) {
        newModel.metrc = null;
      } else if (isEqual(this.biotrackConfig, newModel.biotrack)) {
        newModel.biotrack = null;
      }

      if (newModel.pharmacistData) {
        newModel.pharmacistData.user_id = this.current
          ? Number(this.current!.id)
          : 0;
      }
      this.saveUser(newModel);
      if (this.routeData) {
        this.$router.back();
      }
    }
  }

  public cancel() {
    this.$router.push({ name: "dashboard" });
  }

  public addPharmacistInfoTab() {
    return new Promise(async (resolve, reject) => {
      try {
        const tabIndex =
          this.currentTraceability.biotrackConfiguration === 0
            ? "/2"
            : this.currentTraceability.metrcConfiguration === 0
            ? "/3"
            : "/4";
        const pharmacistTabArray = this.fieldsConfigDetail.filter(el => {
          return el.tab === i18n.t("pharmacist_info") + tabIndex;
        });
        const pharmacistTabConfig = cloneDeep(this.pharmacistTabDetails);
        if (pharmacistTabArray.length === 0) {
          pharmacistTabConfig.map(formData => {
            formData.tab = formData.tab + tabIndex;
          });
          this.fieldsConfigDetail = cloneDeep([
            ...this.fieldsConfigDetail,
            ...pharmacistTabConfig
          ]);
        }
        resolve(true);
      } catch (e) {
        reject();
      }
    });
  }
  public async renderFromTabs() {
    const modelInit = cloneDeep(this.model);
    this.currentTraceability = await traceabilityService.getUserCurrentTraceability(
      this.current!.id!
    );
    const biotrackTraceabilityTabConfig = cloneDeep(
      this.biotrackProfileConfigDetail
    );
    const metrcTraceabilityTabConfig = cloneDeep(this.metrcProfileConfigDetail);
    if (this.currentTraceability.biotrackConfiguration) {
      if (!UserManagementFormComponent.currentBiotrackStatus) {
        const userNameIndex = biotrackTraceabilityTabConfig.findIndex(
          index => index.id === "user_name"
        );
        const inputPassIndex = biotrackTraceabilityTabConfig.findIndex(
          index => index.id === "input_pass"
        );
        biotrackTraceabilityTabConfig![
          userNameIndex
        ]!.options!.validationRules = ["required"];
        biotrackTraceabilityTabConfig![
          inputPassIndex
        ]!.options!.validationRules = ["required"];
      }
      biotrackTraceabilityTabConfig.map(formData => {
        formData.tab = formData.tab + "/2";
      });
      this.fieldsConfigDetail = cloneDeep([
        ...this.fieldsConfigDetail,
        ...biotrackTraceabilityTabConfig
      ]);
    }
    if (this.currentTraceability.metrcConfiguration) {
      metrcTraceabilityTabConfig.map(formData => {
        formData.tab = formData.tab + "/2";
      });
      this.fieldsConfigDetail = cloneDeep([
        ...this.fieldsConfigDetail,
        ...metrcTraceabilityTabConfig
      ]);
    }
    this.fieldsConfigDetail = cloneDeep([...this.fieldsConfigDetail]);

    const pharmacistLocationInfo: number[] = [];

    this.pharmacistDataModel.user_id = this.current ? this.current!.id : 0;

    if (this.pharmacistInfo && this.pharmacistInfo.pharmacist_enable === 1) {
      this.addPharmacistInfoTab().then(() => {
        modelInit.pharmacist = "1";
        if (
          this.pharmacistInfo!.user_location &&
          this.pharmacistInfo!.user_location.length !== 0
        ) {
          this.pharmacistInfo!.user_location.forEach(
            (pharmacistLocation: PharmacistLocationInterface) =>
              pharmacistLocationInfo.push(pharmacistLocation.location_id)
          );
        }
        this.pharmacistDataModel.pharmacist_title = this.pharmacistInfo!.pharmacist_title;
        this.pharmacistDataModel.pharmacist_license_number = this.pharmacistInfo!.pharmacist_license_number;
        this.pharmacistDataModel.pharmacist_national_provider_identifier = this.pharmacistInfo!.pharmacist_national_provider_identifier;
        this.pharmacistDataModel.pharmacist_enable = this.pharmacistInfo!.pharmacist_enable;
        this.pharmacistDataModel.pharmacist_location.add = pharmacistLocationInfo;
        this.pharmacistDataModel.pharmacist_location.delete = pharmacistLocationInfo;
      });
    }
    modelInit.pharmacistData = this.pharmacistDataModel;

    setTimeout(() => {
      this.model = cloneDeep(modelInit);
    }, 50);
  }
  public async mounted() {
    this.getTraceStatus();
    await this.getPharmacistInfo(this.current!.id);
    const modelInit = this.current || this.model;
    this.form = this.$refs["profile-form"] as DynamicFormComponent;
    this.formDetail = this.$refs["profile-detail-form"] as DynamicFormComponent;
    if (!!+process.env.VUE_APP_METRC) {
      this.metrcConfig = await traceabilityService.getUserMetrcConfig(
        this.current!.id!
      );
    }
    this.biotrackConfig = await traceabilityService.getUserBiotrackConfig(
      this.current ? this.current!.id! : 0
    );
    modelInit.hasMetrcConfig = !!this.metrcConfig;
    if (this.metrcConfig) {
      modelInit.metrc = cloneDeep(this.metrcConfig);
    }
    this.model = modelInit;

    modelInit.hasBiotrackConfig = !!this.biotrackConfig;
    if (this.biotrackConfig) {
      modelInit.biotrack = cloneDeep(this.biotrackConfig);
    }
    this.model = modelInit;

    this.renderFromTabs();

    EventBus.$on("metrUserTest", this.showMetrcTestMessage);
    EventBus.$on("biotrackUserTest", this.showBiotrackTestMessage);
  }

  public created() {
    this.routeData = JSON.parse(this.$route.params.data);
  }

  public showMetrcTestMessage(res: boolean) {
    const msgModal: ModalNotification = {
      title: "traceability.integration_form.metrc_enabled",
      body: "traceability.integration_form.mtrc_msg_ok",
      captionButton: "OK",
      titleClass: "success_title"
    };

    if (!res) {
      msgModal.title = "smth_wrong";
      msgModal.body = "traceability.integration_form.metrc_msg_fail";
      msgModal.titleClass = "error_title";
    }
    this.$modals.load(
      AlertComponent,
      { size: "normal", positionX: "center", positionY: "center" },
      { msgModal }
    );
  }
  public showBiotrackTestMessage(res: boolean) {
    const msgModal: ModalNotification = {
      title: "traceability.integration_form.biotrack_enabled",
      body: "traceability.integration_form.biotrack_msg_ok",
      captionButton: "OK",
      titleClass: "success_title"
    };

    if (!res) {
      msgModal.title = "smth_wrong";
      msgModal.body = "traceability.integration_form.biotrack_msg_fail";
      msgModal.titleClass = "error_title";
    }
    this.$modals.load(
      AlertComponent,
      { size: "normal", positionX: "center", positionY: "center" },
      { msgModal }
    );
  }

  public async getTraceStatus() {
    const tracestatus = await traceabilityService.validateUserCredentials();
    UserManagementFormComponent.currentBiotrackStatus =
      tracestatus.status === "error" && tracestatus.code === 200 ? true : false;
  }

  protected getTraceability(locationId: number | null): Promise<string> {
    return traceabilityService
      .getCurrentTraceability({ location_id: locationId })
      .then(value => {
        const config = value.find(v => {
          return (
            v.location_id === this.model.settings!.current_location_id &&
            v.schema.name === "traceability"
          );
        });
        return (config && config.value) || "None";
      });
  }
}
